import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getProfessors, createProfessor, updateProfessor, deleteProfessor,
  getProfessorHours, createProfessorHour, deleteProfessorHour,
  getProfessorRates, upsertProfessorRate,
  getProfessorSummary, updateProfessorSummary,
  getCustomClassTypes, addCustomClassType, deleteCustomClassType
} from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus, Clock, Users, Loader2, Trash2, DollarSign, Edit, X, Copy, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_CLASS_TYPES = [
  { value: 'reformer', label: 'Reformer' },
  { value: 'soft_barre', label: 'Soft Barre' },
  { value: 'recepcion', label: 'Recepcion' },
  { value: 'extra', label: 'Horas Extra' }
];

const HorasProfesores = () => {
  const { isDirection } = useAuth();
  const [professors, setProfessors] = useState([]);
  const [selectedProf, setSelectedProf] = useState(null);
  const [hours, setHours] = useState([]);
  const [rates, setRates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('resumen');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const [showProfDialog, setShowProfDialog] = useState(false);
  const [showHourDialog, setShowHourDialog] = useState(false);
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const [profForm, setProfForm] = useState({ name: '', email: '', phone: '', specialties: [] });
  const [hourForm, setHourForm] = useState({ fecha: '', class_type: 'reformer', cantidad_horas: '', valor_hora: '', observaciones: '' });
  const [rateForm, setRateForm] = useState({ class_type: 'reformer', rate: '' });
  const [editingProf, setEditingProf] = useState(null);
  const [customClassTypes, setCustomClassTypes] = useState([]);
  const [newDiscipline, setNewDiscipline] = useState('');
  const [showDisciplineInput, setShowDisciplineInput] = useState(false);
  const [copiedReport, setCopiedReport] = useState(false);

  const CLASS_TYPES = [...DEFAULT_CLASS_TYPES, ...customClassTypes];

  useEffect(() => { fetchProfessors(); fetchClassTypes(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (professors.length > 0) fetchMonthData(); }, [selectedMonth, professors]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedProf && rates.length > 0) {
      const profRates = rates.filter(r => r.professor_id === selectedProf.id);
      const match = profRates.find(r => r.class_type === hourForm.class_type);
      if (match) setHourForm(f => ({ ...f, valor_hora: match.rate.toString() }));
    }
  }, [hourForm.class_type, selectedProf, rates]);

  const fetchProfessors = async () => {
    try {
      const res = await getProfessors();
      setProfessors(res.data);
      if (res.data.length > 0 && !selectedProf) setSelectedProf(res.data[0]);
    } catch (e) { console.error('Error HorasProfesores:', e.message); }
    finally { setLoading(false); }
  };

  const fetchClassTypes = async () => {
    try {
      const res = await getCustomClassTypes();
      setCustomClassTypes(res.data || []);
    } catch (e) { console.error('Error HorasProfesores:', e.message); }
  };

  const handleAddDiscipline = async () => {
    if (!newDiscipline.trim()) return;
    try {
      await addCustomClassType(newDiscipline.trim());
      toast.success('Disciplina agregada');
      setNewDiscipline('');
      setShowDisciplineInput(false);
      fetchClassTypes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al agregar'); }
  };

  const handleDeleteDiscipline = async (value) => {
    if (!window.confirm('Eliminar esta disciplina?')) return;
    try {
      await deleteCustomClassType(value);
      toast.success('Disciplina eliminada');
      fetchClassTypes();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const copyProfessorReport = () => {
    if (!summary) return;
    const lines = [`RESUMEN HORAS PROFESORES - ${selectedMonth}`, ''];
    summary.summaries.forEach(s => {
      lines.push(`${s.professor_name}: ${s.horas_realizadas != null ? s.horas_realizadas + 'h' : 'N/A'} | Total: ${formatCurrency(s.total_a_transferir)} | ${s.payment_status === 'pago_ok' ? 'PAGADO' : 'PENDIENTE'}`);
    });
    lines.push('', `TOTAL A TRANSFERIR: ${formatCurrency(summary.grand_total_transferir)}`);
    navigator.clipboard.writeText(lines.join('\n'));
    setCopiedReport(true);
    toast.success('Resumen copiado');
    setTimeout(() => setCopiedReport(false), 2000);
  };

  const fetchMonthData = async () => {
    try {
      const [hoursRes, ratesRes, summaryRes] = await Promise.all([
        getProfessorHours({ month: selectedMonth }),
        getProfessorRates({ month: selectedMonth }),
        getProfessorSummary(selectedMonth)
      ]);
      setHours(hoursRes.data);
      setRates(ratesRes.data);
      setSummary(summaryRes.data);
    } catch (e) { console.error('Error HorasProfesores:', e.message); }
  };

  const handleSaveProf = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...profForm, specialties: profForm.specialties || [], active: true };
      if (editingProf) {
        await updateProfessor(editingProf.id, data);
        toast.success('Profesora actualizada');
      } else {
        await createProfessor(data);
        toast.success('Profesora creada');
      }
      setShowProfDialog(false);
      setEditingProf(null);
      fetchProfessors();
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDeleteProf = async (id) => {
    if (!window.confirm('Eliminar esta profesora?')) return;
    try {
      await deleteProfessor(id);
      toast.success('Profesora eliminada');
      fetchProfessors();
      if (selectedProf?.id === id) setSelectedProf(null);
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const handleSaveHour = async (e) => {
    e.preventDefault();
    if (!selectedProf) return;
    setSaving(true);
    try {
      await createProfessorHour({
        professor_id: selectedProf.id,
        month: selectedMonth,
        fecha: hourForm.fecha,
        class_type: hourForm.class_type,
        cantidad_horas: parseFloat(hourForm.cantidad_horas),
        valor_hora: parseFloat(hourForm.valor_hora),
        observaciones: hourForm.observaciones || null
      });
      toast.success('Registro creado');
      setShowHourDialog(false);
      setHourForm({ fecha: '', class_type: 'reformer', cantidad_horas: '', valor_hora: '', observaciones: '' });
      fetchMonthData();
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDeleteHour = async (id) => {
    try {
      await deleteProfessorHour(id);
      toast.success('Registro eliminado');
      fetchMonthData();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const handleSaveRate = async (e) => {
    e.preventDefault();
    if (!selectedProf) return;
    setSaving(true);
    try {
      await upsertProfessorRate({
        professor_id: selectedProf.id,
        month: selectedMonth,
        class_type: rateForm.class_type,
        rate: parseFloat(rateForm.rate)
      });
      toast.success('Tarifa guardada');
      setShowRateDialog(false);
      fetchMonthData();
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleUpdateSummaryField = async (professorId, field, value) => {
    try {
      await updateProfessorSummary(professorId, selectedMonth, { [field]: value });
      toast.success('Actualizado');
      fetchMonthData();
    } catch (e) { toast.error('Error al actualizar'); }
  };

  const boletaLabel = (s) => {
    if (s === 'no_aplica') return 'No Aplica';
    if (s === 'emitida') return 'Emitida';
    return 'Pendiente';
  };
  const boletaClass = (s) => {
    if (s === 'no_aplica') return 'bg-gray-100 text-gray-500';
    if (s === 'emitida') return 'status-activo';
    return 'status-pendiente';
  };
  const boletaNext = (s) => {
    if (s === 'pendiente') return 'emitida';
    if (s === 'emitida') return 'no_aplica';
    return 'pendiente';
  };

  if (!isDirection()) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Clock className="mx-auto h-12 w-12 text-[#E8E2D7] mb-4" />
          <h2 className="text-xl font-semibold text-[#1A2835]">Acceso Restringido</h2>
          <p className="text-[#5C7083]">Solo Dirección puede acceder a este módulo</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" /></div>;
  }

  const profHours = selectedProf ? hours.filter(h => h.professor_id === selectedProf.id) : [];
  const profRates = selectedProf ? rates.filter(r => r.professor_id === selectedProf.id) : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="horas-profesores-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Control de Horas Profesores</h1>
          <p className="page-subtitle">Registro y control mensual de horas trabajadas</p>
        </div>
        <div className="flex items-center gap-3">
          <Input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="form-input w-auto" data-testid="select-month" />
          <Button className="btn-primary gap-2" onClick={() => { setEditingProf(null); setProfForm({ name: '', email: '', phone: '', specialties: [] }); setShowProfDialog(true); }} data-testid="btn-add-professor">
            <Plus size={18} /> Profesora
          </Button>
        </div>
      </div>

      {professors.length === 0 ? (
        <div className="card text-center py-12">
          <Users className="mx-auto h-12 w-12 text-[#E8E2D7] mb-4" />
          <p className="text-[#5C7083]">No hay profesoras registradas.</p>
        </div>
      ) : (
        <>
          <div className="flex gap-2 flex-wrap" data-testid="professor-tabs">
            {professors.map(p => (
              <div key={p.id} className="flex items-center gap-0.5">
                <Button variant={selectedProf?.id === p.id ? 'default' : 'outline'}
                  className={`${selectedProf?.id === p.id ? 'bg-[#2B4257] text-white' : 'btn-secondary'} rounded-r-none ${p.status === 'inactivo' ? 'opacity-50' : ''}`}
                  onClick={() => setSelectedProf(p)} data-testid={`prof-tab-${p.id}`}>
                  {p.name}{p.status === 'inactivo' ? ' (inactiva)' : ''}
                </Button>
                {p.status !== 'inactivo' && (
                  <Button variant="outline" size="sm"
                    className="rounded-l-none px-1.5 h-9 text-[#C25953] hover:bg-[#FBEAE9] border-l-0"
                    onClick={() => handleDeleteProf(p.id)} data-testid={`btn-delete-prof-${p.id}`}>
                    <X size={14} />
                  </Button>
                )}
              </div>
            ))}
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white border border-[#E8E2D7] p-1 rounded-lg">
              <TabsTrigger value="resumen" className="data-[state=active]:bg-[#2B4257] data-[state=active]:text-white rounded-md px-4">Resumen General</TabsTrigger>
              <TabsTrigger value="detalle" className="data-[state=active]:bg-[#2B4257] data-[state=active]:text-white rounded-md px-4">Detalle Horas</TabsTrigger>
              <TabsTrigger value="tarifas" className="data-[state=active]:bg-[#2B4257] data-[state=active]:text-white rounded-md px-4">Tarifas</TabsTrigger>
              <TabsTrigger value="disciplinas" className="data-[state=active]:bg-[#2B4257] data-[state=active]:text-white rounded-md px-4">Disciplinas</TabsTrigger>
            </TabsList>

            {/* RESUMEN GENERAL - Matches Excel exactly */}
            <TabsContent value="resumen" className="mt-4">
              {summary && (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <Button variant="outline" className="btn-secondary gap-2" onClick={copyProfessorReport} data-testid="btn-copy-prof-report">
                      {copiedReport ? <CheckCircle size={14} /> : <Copy size={14} />}
                      {copiedReport ? 'Copiado' : 'Copiar Resumen'}
                    </Button>
                  </div>
                  <div className="card p-0 overflow-hidden" data-testid="summary-table">
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Horas Realizadas</th>
                          <th>Valor Hora</th>
                          <th>Total a Transferir</th>
                          <th>Imponible</th>
                          <th>Boleta</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.summaries.map(s => (
                          <tr key={s.professor_id} data-testid={`summary-row-${s.professor_id}`}>
                            <td className="font-medium text-[#1A2835]">{s.professor_name}</td>
                            <td>{s.horas_realizadas != null ? s.horas_realizadas : <span className="text-[#5C7083] text-xs">NO APLICA</span>}</td>
                            <td>{s.valor_hora != null ? formatCurrency(s.valor_hora) : <span className="text-[#5C7083] text-xs">NO APLICA</span>}</td>
                            <td className="font-semibold text-[#2B4257]">{formatCurrency(s.total_a_transferir)}</td>
                            <td className="font-semibold">{formatCurrency(s.imponible)}</td>
                            <td>
                              <button className={`status-badge cursor-pointer ${boletaClass(s.boleta_status)}`}
                                onClick={() => handleUpdateSummaryField(s.professor_id, 'boleta_status', boletaNext(s.boleta_status))}
                                data-testid={`boleta-toggle-${s.professor_id}`}>
                                {boletaLabel(s.boleta_status)}
                              </button>
                            </td>
                            <td>
                              <button className={`status-badge cursor-pointer ${s.payment_status === 'pago_ok' ? 'status-activo' : 'status-pendiente'}`}
                                onClick={() => handleUpdateSummaryField(s.professor_id, 'payment_status', s.payment_status === 'pago_ok' ? 'pendiente' : 'pago_ok')}
                                data-testid={`pago-toggle-${s.professor_id}`}>
                                {s.payment_status === 'pago_ok' ? 'Pago OK' : 'Pendiente'}
                              </button>
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-[#F5F0E8] font-bold">
                          <td>TOTAL</td>
                          <td colSpan={2}></td>
                          <td className="text-[#2B4257]">{formatCurrency(summary.grand_total_transferir)}</td>
                          <td className="text-[#1A2835]">{formatCurrency(summary.grand_total_imponible)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                </div>
              )}
            </TabsContent>

            {/* DETALLE HORAS */}
            <TabsContent value="detalle" className="mt-4">
              {selectedProf && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-[#1A2835]">Horas de {selectedProf.name}</h3>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="btn-secondary gap-2" onClick={() => {
                        setEditingProf(selectedProf);
                        setProfForm({ name: selectedProf.name, email: selectedProf.email || '', phone: selectedProf.phone || '', specialties: selectedProf.specialties || [] });
                        setShowProfDialog(true);
                      }} data-testid="btn-edit-prof">
                        <Edit size={14} /> Editar
                      </Button>
                      <Button className="btn-primary gap-2" onClick={() => setShowHourDialog(true)} data-testid="btn-add-hour">
                        <Plus size={18} /> Registrar Horas
                      </Button>
                    </div>
                  </div>
                  <div className="card p-0 overflow-hidden" data-testid="hours-table">
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Fecha</th>
                            <th>Tipo Clase</th>
                            <th>Cantidad Horas</th>
                            <th>Valor Hora</th>
                            <th>Total</th>
                            <th>Observaciones</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {profHours.length === 0 ? (
                            <tr><td colSpan={7} className="text-center py-8 text-[#5C7083]">No hay registros para este mes</td></tr>
                          ) : (
                            profHours.map(h => (
                              <tr key={h.id} data-testid={`hour-row-${h.id}`}>
                                <td>{h.fecha}</td>
                                <td><span className="status-badge badge-default">{CLASS_TYPES.find(ct => ct.value === h.class_type)?.label || h.class_type}</span></td>
                                <td className="font-medium">{h.cantidad_horas}</td>
                                <td>{formatCurrency(h.valor_hora)}</td>
                                <td className="font-semibold">{formatCurrency(h.total || h.cantidad_horas * h.valor_hora)}</td>
                                <td className="text-sm text-[#5C7083] max-w-[200px] truncate">{h.observaciones || '-'}</td>
                                <td>
                                  <Button variant="ghost" size="sm" className="btn-ghost p-1 text-[#C25953]" onClick={() => handleDeleteHour(h.id)} data-testid={`btn-delete-hour-${h.id}`}>
                                    <Trash2 size={14} />
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {profHours.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="card text-center">
                        <p className="text-sm text-[#5C7083]">Horas Clase</p>
                        <p className="text-2xl font-bold text-[#2B4257]">{profHours.filter(h => !['recepcion','extra'].includes(h.class_type)).reduce((s, h) => s + h.cantidad_horas, 0)}</p>
                      </div>
                      <div className="card text-center">
                        <p className="text-sm text-[#5C7083]">Horas Recepción</p>
                        <p className="text-2xl font-bold text-[#D98A6C]">{profHours.filter(h => h.class_type === 'recepcion').reduce((s, h) => s + h.cantidad_horas, 0)}</p>
                      </div>
                      <div className="card text-center">
                        <p className="text-sm text-[#5C7083]">Horas Extra</p>
                        <p className="text-2xl font-bold text-[#4A7C59]">{profHours.filter(h => h.class_type === 'extra').reduce((s, h) => s + h.cantidad_horas, 0)}</p>
                      </div>
                      <div className="card text-center">
                        <p className="text-sm text-[#5C7083]">Total</p>
                        <p className="text-2xl font-bold text-[#2B4257]">{formatCurrency(profHours.reduce((s, h) => s + (h.total || h.cantidad_horas * h.valor_hora), 0))}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* TARIFAS */}
            <TabsContent value="tarifas" className="mt-4">
              {selectedProf && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-[#1A2835]">Tarifas de {selectedProf.name} - {selectedMonth}</h3>
                    <Button className="btn-primary gap-2" onClick={() => { setRateForm({ class_type: 'reformer', rate: '' }); setShowRateDialog(true); }} data-testid="btn-add-rate">
                      <DollarSign size={18} /> Definir Tarifa
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {CLASS_TYPES.map(ct => {
                      const rate = profRates.find(r => r.class_type === ct.value);
                      return (
                        <div key={ct.value} className="card" data-testid={`rate-card-${ct.value}`}>
                          <p className="text-sm text-[#5C7083] mb-1">{ct.label}</p>
                          <p className="text-2xl font-bold text-[#2B4257]">
                            {rate ? formatCurrency(rate.rate) : <span className="text-[#E8E2D7]">Sin definir</span>}
                          </p>
                          <p className="text-xs text-[#5C7083]">por hora</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* DISCIPLINAS */}
            <TabsContent value="disciplinas" className="mt-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-[#1A2835]">Tipos de Clase / Disciplinas</h3>
                  {!showDisciplineInput && (
                    <Button className="btn-primary gap-2" onClick={() => setShowDisciplineInput(true)} data-testid="btn-add-discipline">
                      <Plus size={18} /> Nueva Disciplina
                    </Button>
                  )}
                </div>
                {showDisciplineInput && (
                  <div className="card flex items-center gap-3">
                    <Input value={newDiscipline} onChange={e => setNewDiscipline(e.target.value)}
                      placeholder="Nombre de la disciplina (ej: Yoga, Mat Pilates)"
                      className="flex-1" data-testid="input-new-discipline"
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddDiscipline(); } }} />
                    <Button className="btn-primary" onClick={handleAddDiscipline} disabled={!newDiscipline.trim()} data-testid="btn-confirm-discipline">Agregar</Button>
                    <Button variant="outline" className="btn-secondary" onClick={() => { setShowDisciplineInput(false); setNewDiscipline(''); }}>Cancelar</Button>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {DEFAULT_CLASS_TYPES.map(ct => (
                    <div key={ct.value} className="card flex items-center justify-between p-4">
                      <div>
                        <p className="font-medium text-[#1A2835]">{ct.label}</p>
                        <p className="text-xs text-[#5C7083]">Predefinida</p>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#F5F0E8] text-[#5C7083]">Base</span>
                    </div>
                  ))}
                  {customClassTypes.map(ct => (
                    <div key={ct.value} className="card flex items-center justify-between p-4">
                      <div>
                        <p className="font-medium text-[#1A2835]">{ct.label}</p>
                        <p className="text-xs text-[#5C7083]">Personalizada</p>
                      </div>
                      <Button variant="ghost" size="sm" className="text-[#C25953] hover:bg-[#FBEAE9] p-1"
                        onClick={() => handleDeleteDiscipline(ct.value)} data-testid={`btn-delete-discipline-${ct.value}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Professor Dialog */}
      <Dialog open={showProfDialog} onOpenChange={setShowProfDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingProf ? 'Editar Profesora' : 'Nueva Profesora'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveProf} className="space-y-4" data-testid="professor-form">
            <div>
              <Label className="form-label">Nombre *</Label>
              <Input value={profForm.name} onChange={e => setProfForm({ ...profForm, name: e.target.value })} className="form-input" required data-testid="input-prof-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Email</Label>
                <Input value={profForm.email} onChange={e => setProfForm({ ...profForm, email: e.target.value })} className="form-input" data-testid="input-prof-email" />
              </div>
              <div>
                <Label className="form-label">Teléfono</Label>
                <Input value={profForm.phone} onChange={e => setProfForm({ ...profForm, phone: e.target.value })} className="form-input" data-testid="input-prof-phone" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowProfDialog(false)} className="btn-secondary">Cancelar</Button>
              <Button type="submit" className="btn-primary" disabled={saving || !profForm.name} data-testid="btn-save-prof">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingProf ? 'Guardar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hour Entry Dialog */}
      <Dialog open={showHourDialog} onOpenChange={setShowHourDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar Horas - {selectedProf?.name}</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveHour} className="space-y-4" data-testid="hour-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Fecha *</Label>
                <Input type="date" value={hourForm.fecha} onChange={e => setHourForm({ ...hourForm, fecha: e.target.value })} className="form-input" required data-testid="input-hour-fecha" />
              </div>
              <div>
                <Label className="form-label">Tipo *</Label>
                <select value={hourForm.class_type} onChange={e => setHourForm({ ...hourForm, class_type: e.target.value })} className="form-input" data-testid="select-hour-type">
                  {CLASS_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Cantidad Horas *</Label>
                <Input type="number" step="0.5" value={hourForm.cantidad_horas} onChange={e => setHourForm({ ...hourForm, cantidad_horas: e.target.value })} className="form-input" required data-testid="input-hour-cantidad" />
              </div>
              <div>
                <Label className="form-label">Valor Hora *</Label>
                <Input type="number" value={hourForm.valor_hora} onChange={e => setHourForm({ ...hourForm, valor_hora: e.target.value })} className="form-input" required data-testid="input-hour-valor" />
              </div>
            </div>
            <div>
              <Label className="form-label">Observaciones</Label>
              <textarea value={hourForm.observaciones} onChange={e => setHourForm({ ...hourForm, observaciones: e.target.value })} className="form-input min-h-[60px] resize-none" data-testid="input-hour-obs" />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowHourDialog(false)} className="btn-secondary">Cancelar</Button>
              <Button type="submit" className="btn-primary" disabled={saving || !hourForm.fecha || !hourForm.cantidad_horas || !hourForm.valor_hora} data-testid="btn-save-hour">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Registrar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rate Dialog */}
      <Dialog open={showRateDialog} onOpenChange={setShowRateDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Definir Tarifa - {selectedProf?.name}</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveRate} className="space-y-4" data-testid="rate-form">
            <div>
              <Label className="form-label">Tipo de Clase</Label>
              <select value={rateForm.class_type} onChange={e => setRateForm({ ...rateForm, class_type: e.target.value })} className="form-input" data-testid="select-rate-type">
                {CLASS_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="form-label">Valor Hora ($)</Label>
              <Input type="number" value={rateForm.rate} onChange={e => setRateForm({ ...rateForm, rate: e.target.value })} className="form-input" required data-testid="input-rate-value" />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowRateDialog(false)} className="btn-secondary">Cancelar</Button>
              <Button type="submit" className="btn-primary" disabled={saving || !rateForm.rate} data-testid="btn-save-rate">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Guardar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HorasProfesores;
